import { subYears } from 'date-fns';
import orderBy from 'lodash/orderBy';
import sortBy from 'lodash/sortBy';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Skeleton from 'react-loading-skeleton';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, Col, Container, Form, Input, Label, Row, UncontrolledTooltip } from 'reactstrap';
import { ApplicationRoutes, AppSettings, AttributeCategoriesText, ProductNames, Products } from '../../../../constants';
import useNavigationService from '../../../../hooks/UseNavigationService';
import AdvisorsApiService from '../../../../services/api/AdvisorsApiService';
import UsersApiService from '../../../../services/api/UsersApiService';
import { ErrorLogService } from '../../../../services/ErrorLogService';
import AdvisorReviews from '../../../Advisor/AdvisorReviews';
import AuthorizeService from '../../../api-authorization/AuthorizeService';
import { DisclaimerRow } from '../../../DisclaimerRow/DisclaimerRow';
import AdvisorsHelper from '../../../Helpers/AdvisorsHelper';
import DateHelper from '../../../Helpers/DateHelper';
import ErrorsHelper from '../../../Helpers/ErrorsHelper';
import StateHelper from '../../../Helpers/StateHelper';
import { FAQs } from '../../../HomeElements/FAQs';
import { useLoadingOverlayContext } from '../../../ui/LoadingAnimations/LoadingOverlay/LoadingOverlayContext';
import LoadingSpinner from '../../../ui/LoadingAnimations/LoadingSpinner/LoadingSpinner';
import NectarineButton from '../../../ui/NectarineButton/NectarineButton';
import AdvisorEditPersonalPhoto from '../../Account/Advisor/AdvisorEditPersonalPhoto/AdvisorEditPersonalPhoto';
import AdvisorEditProfilePhoto from '../../Account/Advisor/AdvisorEditProfilePhoto/AdvisorEditProfilePhoto';
import './AdvisorDetails.scss';
import AdvisorDetailsSkeleton from './AdvisorDetailsSkeleton';
import { AdvisorEditAttributes } from './AdvisorEditAttributes/AdvisorEditAttributes';
import AdvisorEditBio from './AdvisorEditBio/AdvisorEditBio';
import AdvisorEditCustomAttributes from './AdvisorEditCustomAttributes/AdvisorEditCustomAttributes';

const filteredAttributesSessionKey = 'filteredAttributes';
const stateDropdownAbbreviation_NotUS = 'non-us';

const stateDropdownOptions = [
  {
    name: `I don't live in the US`,
    abbreviation: stateDropdownAbbreviation_NotUS
  },
  ...StateHelper.usStatesAndTerritories
];

const planOptions: {
  title: string;
  bulletPoints: string[];
  price: number;
  priceSuffix: string;
  nextAvailabilityString: string | null;
  learnMoreText?: string;
}[] = [
  {
    title: ProductNames.TheHour,
    bulletPoints: ['Ask a financial advisor anything about investing in one hour', 'No commissions, products, or sales pitch'],
    price: 0,
    priceSuffix: '/ one hour',
    nextAvailabilityString: null
  },
  {
    title: ProductNames.ThePlan,
    bulletPoints: [
      'An 8 week program to achieve a strategic plan for your financial life',
      'Get an 8+ page financial plan',
      '$0 due now, pay after your 1st meeting'
    ],
    price: 0,
    priceSuffix: '/ eight weeks',
    nextAvailabilityString: null,
    learnMoreText: 'Learn more about The Plan'
  }
];

const AdvisorDetails = () => {
  const componentName = 'AdvisorDetails';

  const { slug } = useParams();

  const { navigateTo_Error } = useNavigationService();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlayContext();

  const [advisor, setAdvisor] = useState<Advisor_BasicDetails_ViewModel | null>(null);
  const [consumer, setConsumer] = useState<User_BasicDetails_ViewModel | null>(null);

  const [redirectTo, setRedirectTo] = useState<boolean>(false);
  const [errorSection, setErrorSection] = useState<ErrorSection>({ show: false, text: null });
  const [agreeIsChecked, setAgreeIsChecked] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [state, setState] = useState<string | null>(localStorage.getItem('userState'));
  const [stateIsInUS, setStateIsInUS] = useState<boolean>(false);
  const [editBioModal, setEditBioModal] = useState<boolean>(false);
  const [editAttributesModal, setEditAttributesModal] = useState<boolean>(false);
  const [editEducationModal, setEditEducationModal] = useState<boolean>(false);
  const [editExperienceModal, setEditExperienceModal] = useState<boolean>(false);
  const [editPhotoModal, setEditPhotoModal] = useState<boolean>(false);
  const [editProfilePicModal, setEditProfilePicModal] = useState<boolean>(false);
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [selectedPlanOption, setSelectedPlanOption] = useState<string>(planOptions[0].title);

  const toggleEditBioModal = () => setEditBioModal(!editBioModal);
  const toggleEditPhotoModal = () => setEditPhotoModal(!editPhotoModal);
  const toggleEditProfilePicModal = () => setEditProfilePicModal(!editProfilePicModal);
  const toggleEditAttributesModal = () => setEditAttributesModal(!editAttributesModal);
  const toggleEditEducationModal = () => setEditEducationModal(!editEducationModal);

  const toggleEditExperienceModal = () => setEditExperienceModal(!editExperienceModal);

  const now = new Date();
  const fiftyYearsAgo = subYears(now, 50);

  useEffect(() => {
    setStateIsInUS(state !== stateDropdownAbbreviation_NotUS);
  }, [state]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const isAuthenticatedResult = await AuthorizeService.isAuthenticated();
        setIsAuthenticated(isAuthenticatedResult);

        const userViewModel = await AuthorizeService.populateActiveUser();
        setConsumer(userViewModel);

        const advisorObj = await AdvisorsApiService.getAdvisorDetailsByIdOrSlug(slug ?? '', true, true, false);
        advisorObj.experienceStart = advisorObj.experienceStart ? new Date(advisorObj.experienceStart) : null;
        setAdvisor(advisorObj);

        planOptions[0].price = advisorObj.hourlyRate;
        planOptions[1].price = advisorObj.thePlanRate > 0 ? advisorObj.thePlanRate : 2500;

        planOptions[0].nextAvailabilityString =
          advisorObj.nextAvailability && new Date(advisorObj.nextAvailability) >= now
            ? DateHelper.casualDateFormat(advisorObj.nextAvailability)
            : null;

        planOptions[1].nextAvailabilityString = advisorObj.nextThePlanAvailability
          ? DateHelper.fullMonthAndYear(advisorObj.nextThePlanAvailability)
          : null;
      } catch (error) {
        ErrorLogService.logError(`${componentName} - loadData()`, 'An error occurred while loading page data.', error);
        navigateTo_Error();
      }
    };
    loadData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadAdvisorDetails = async () => {
    const advisorObj = await AdvisorsApiService.getAdvisorDetailsByIdOrSlug(slug ?? '', true, true, false);
    advisorObj.experienceStart = advisorObj.experienceStart ? new Date(advisorObj.experienceStart) : null;
    setAdvisor(advisorObj);
  };

  useEffect(() => {
    if (isAuthenticated && consumer && advisor && consumer.id == advisor.id) {
      setCanEdit(true);
    }
  }, [isAuthenticated, consumer, advisor]);

  // NOTE: This MUST come after any useEffects, or it violates the rule of hooks and React breaks
  if (redirectTo) {
    if (selectedPlanOption === ProductNames.TheHour) {
      window.location.href = getRedirectLinkForTheHour();
      return <LoadingSpinner message="Looking up calendar..." />;
    } else {
      window.location.href = ApplicationRoutes.ThePlan_Signup;
    }
  }

  function closeAllModals() {
    setEditBioModal(false);
    setEditAttributesModal(false);
    setEditEducationModal(false);
    setEditExperienceModal(false);
    setEditPhotoModal(false);
    setEditProfilePicModal(false);
  }

  function isScheduleButtonEnabled() {
    return stateIsInUS && agreeIsChecked;
  }

  function docSeparator(i: number, length: number) {
    if (i == 0 && length == 2) {
      return <span> and </span>;
    }
    if (i < length - 2) {
      return <span>, </span>;
    }
    if (i < length - 1) {
      return <span>, and </span>;
    }

    return <span> </span>;
  }

  const onStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    setState(value);

    if (value !== stateDropdownAbbreviation_NotUS) {
      localStorage.setItem('userState', e.target.value);
    }
  };

  async function onScheduleClick(e: React.FormEvent, state: string) {
    e.preventDefault();
    hideError();

    try {
      showLoadingOverlay();

      await UsersApiService.agreeToPlanTerms(advisor!.id, {
        product: selectedPlanOption === ProductNames.TheHour ? Products.TheHour : Products.ThePlan,
        stateAbbreviation: state,
        additionalComplianceDocumentIds: []
      });

      setRedirectTo(true);
    } catch (error: any) {
      showError(ErrorsHelper.getErrorMessageFromErrorObject(error.message));
    } finally {
      hideLoadingOverlay();
    }

    return false;
  }

  function showError(text: string): void {
    setErrorSection({ show: true, text });
  }

  function hideError(): void {
    setErrorSection({ show: false, text: null });
  }

  function getRedirectLinkForTheHour(): string {
    // const params = new URLSearchParams(window.location.search);

    if (!consumer || !advisor) {
      return '';
    }

    const outputParams = new URLSearchParams();
    outputParams.set('email', consumer.email);
    outputParams.set('name', consumer.firstName + ' ' + consumer.lastName);

    return advisor.calendarLink + '?' + outputParams.toString();
  }

  function getResultsLink() {
    let url = `/${ApplicationRoutes.Advisors}`;

    const filteredAttributesSessionDataString = sessionStorage.getItem(filteredAttributesSessionKey);
    const filteredAttributesSessionData = filteredAttributesSessionDataString ? JSON.parse(filteredAttributesSessionDataString) : null;

    if (filteredAttributesSessionData && filteredAttributesSessionData.length) {
      url += '?filters=' + filteredAttributesSessionData.join('+');
    }

    return url;
  }

  function onSelectedPlanOptionClick(title: string): void {
    setSelectedPlanOption(title);

    // Reset the agree checkbox
    setAgreeIsChecked(false);
  }

  return (
    <Container>
      <Helmet>
        {advisor ? <title>{AdvisorsHelper.getAdvisorDisplayName(advisor)} - Fiduciary Financial Advisor</title> : <div></div>}

        {!advisor ? <title>Loading... - {AppSettings.ApplicationName}</title> : <div></div>}
      </Helmet>

      <Row>
        <Link to={getResultsLink()}>
          <i className="c-black fa-regular fa-arrow-left back-arrow"></i> Back
        </Link>
      </Row>

      {!advisor ? <AdvisorDetailsSkeleton /> : <></>}

      {advisor ? (
        <Row className="advisor-details">
          <Col xl="4" lg="5">
            <div className="checkout-box position-relative">
              {canEdit ? (
                <div>
                  <Button className="end-0 position-absolute me-3" color="light" onClick={toggleEditProfilePicModal}>
                    <i className="fa-light fa-pen-to-square"></i> Edit Profile Pic
                  </Button>

                  <AdvisorEditProfilePhoto
                    show={editProfilePicModal}
                    toggle={toggleEditProfilePicModal}
                    save={async () => {
                      closeAllModals();
                      await reloadAdvisorDetails();
                    }}
                    advisor={advisor}
                  />
                </div>
              ) : (
                <></>
              )}

              {advisor.profilePicUrl ? (
                <div className="text-center">
                  <img src={advisor.profilePicUrl} className="circle-image headshot" alt={AdvisorsHelper.getAdvisorDisplayName(advisor)} />
                </div>
              ) : (
                <div className="text-center">
                  <span className="fa-duotone fa-solid fa-circle-user font-rem-10 c-blue"></span>
                </div>
              )}

              <h2 className="text-center">{AdvisorsHelper.getAdvisorDisplayNameJSX(advisor, 'lg')}</h2>
              <h3 className="text-center">{advisor.title}</h3>

              <div className="planOptions mt-4">
                {planOptions.map((currPlan) => {
                  return (
                    <React.Fragment key={currPlan.title}>
                      <div
                        className={`planOption mt-3 ${selectedPlanOption === currPlan.title ? 'isSelected' : ''}`}
                        onClick={() => {
                          onSelectedPlanOptionClick(currPlan.title);
                        }}
                      >
                        <h2>{currPlan.title}</h2>

                        <div className="radioButton"></div>

                        <ul className="mb-2">
                          {currPlan.bulletPoints.map((currBulletPoint) => {
                            return <li key={currBulletPoint}>{currBulletPoint}</li>;
                          })}
                        </ul>

                        {currPlan.price > 0 ? (
                          <>
                            <div className="d-flex jcc aic gap-2">
                              <span className="price">${Math.round(currPlan.price).toLocaleString('en-US')}</span>

                              <span className="c-gray font-l2">{currPlan.priceSuffix}</span>
                            </div>
                          </>
                        ) : (
                          <Skeleton width={80} height={45} inline={true} />
                        )}

                        {currPlan.nextAvailabilityString ? (
                          <div className="next-available mt-1 d-flex jcc aic gap-2">
                            <i className="fa-regular fa-clock att-icon"></i> Next Available:{' '}
                            <strong>{currPlan.nextAvailabilityString}</strong>
                          </div>
                        ) : (
                          <></>
                        )}
                      </div>

                      {currPlan.learnMoreText ? (
                        <Link to={`/${ApplicationRoutes.ThePlan}`} className="learnMoreLink mt-2">
                          <i className="fa-light fa-circle-question me-2"></i>
                          <div className="text-center c-gray">{currPlan.learnMoreText}</div>
                        </Link>
                      ) : (
                        <> </>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {!isAuthenticated ? (
                <div>
                  <Row className="text-center">
                    <Link to={`/${ApplicationRoutes.Register}?advisor=${advisor.slug}`} className="btn btn-primary btn-lg schedule-btn">
                      Schedule an appointment
                    </Link>
                  </Row>
                </div>
              ) : (
                <></>
              )}

              {isAuthenticated ? (
                <Form onSubmit={(e) => onScheduleClick(e, state!)}>
                  <Row className="text-center agree-section">
                    <Label for="state" className="state-label">
                      Which state do you live in?
                      <i id="notUsTooltipIcon" className="ms-2 light-blue fa-regular fa-circle-info"></i>
                      <UncontrolledTooltip target="notUsTooltipIcon">
                        We ask for your state so we can stay in compliance with state and federal regulations. Currently our services are
                        only available to US residents.
                      </UncontrolledTooltip>
                    </Label>

                    <div className="d-flex jcc">
                      <select id="state" name="state" className="state-search" required onChange={onStateChange} value={state ?? undefined}>
                        <option value="">Which state do you live in?</option>
                        {stateDropdownOptions.map((state) => (
                          <option key={state.abbreviation} value={state.abbreviation}>
                            {state.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="d-flex mt-3 gap-1">
                      <Input
                        id="agreeToTerms"
                        name="agreeToTerms"
                        type="checkbox"
                        onChange={() => {
                          setAgreeIsChecked((v) => !v);
                        }}
                        checked={agreeIsChecked}
                      />

                      {selectedPlanOption === ProductNames.TheHour ? (
                        <Label for="agreeToTerms">
                          I agree to the
                          {advisor.nectarineHourComplianceDocuments.map((v, i) => (
                            <span key={v.displayName}>
                              {' '}
                              <a href={v.url} target="_blank" rel="noopener noreferrer">
                                {v.displayName}
                              </a>
                              {docSeparator(i, advisor.nectarineHourComplianceDocuments.length)}
                            </span>
                          ))}
                          of Nectarine
                          {advisor.advisorHourComplianceDocuments.length > 0 ? (
                            <span>
                              {' '}
                              and the
                              {advisor.advisorHourComplianceDocuments.map((currDocument, i) => (
                                <span key={currDocument.id}>
                                  {' '}
                                  <a href={currDocument.url} target="_blank" rel="noopener noreferrer">
                                    {currDocument.displayName}
                                  </a>
                                  {docSeparator(i, advisor.advisorHourComplianceDocuments.length)}
                                </span>
                              ))}
                            </span>
                          ) : (
                            <></>
                          )}
                        </Label>
                      ) : (
                        <></>
                      )}

                      {selectedPlanOption === ProductNames.ThePlan ? (
                        <Label for="agreeToTerms">
                          I agree to the
                          {advisor.nectarinePlanComplianceDocuments.map((currDocument, i) => (
                            <span key={i}>
                              {' '}
                              <a href={currDocument.url} target="_blank" rel="noopener noreferrer">
                                {currDocument.displayName}
                              </a>
                              {docSeparator(i, advisor.nectarinePlanComplianceDocuments.length)}
                            </span>
                          ))}
                          of Nectarine
                          {advisor.advisorPlanComplianceDocuments.length > 0 ? (
                            <span>
                              {' '}
                              and the
                              {advisor.advisorPlanComplianceDocuments.map((currDocument, i) => (
                                <span key={currDocument.id}>
                                  {' '}
                                  <a href={currDocument.url} target="_blank" rel="noopener noreferrer">
                                    {currDocument.displayName}
                                  </a>
                                  {docSeparator(i, advisor.advisorPlanComplianceDocuments.length)}
                                </span>
                              ))}
                            </span>
                          ) : (
                            <></>
                          )}
                        </Label>
                      ) : (
                        <></>
                      )}
                    </div>
                  </Row>

                  {!stateIsInUS ? (
                    <Alert color="danger" className="mt-4">
                      Currently our services are only available to US residents. &nbsp;
                      <Link className="mt-4" to={`/${ApplicationRoutes.NonUS}`}>
                        Learn more.
                      </Link>
                    </Alert>
                  ) : (
                    <></>
                  )}

                  {errorSection.show && (
                    <Alert color="danger" className="mt-4">
                      {errorSection.text}
                    </Alert>
                  )}

                  <div className="text-center">
                    <Button disabled={!isScheduleButtonEnabled()} size="md" className="btn btn-primary btn-lg schedule-btn">
                      {selectedPlanOption === ProductNames.TheHour ? (
                        <>View Availability</>
                      ) : (
                        <>Sign up - ${Math.round(advisor.thePlanRate).toLocaleString('en-US')} ($0 due upfront)</>
                      )}
                    </Button>
                  </div>
                </Form>
              ) : (
                <></>
              )}

              {advisor ? (
                <div className="text-center mt-3">
                  {selectedPlanOption === ProductNames.TheHour && advisor.nextAvailability && new Date(advisor.nextAvailability!) > now ? (
                    <div className="next-available">
                      <i className="fa-regular fa-clock att-icon"></i> Next Available:{' '}
                      <strong>{DateHelper.casualDateFormat(advisor.nextAvailability!)}</strong>
                    </div>
                  ) : (
                    <></>
                  )}

                  {selectedPlanOption === ProductNames.ThePlan && advisor.nextThePlanAvailability ? (
                    <div className="next-available">
                      <i className="fa-regular fa-clock att-icon"></i> Next Available:{' '}
                      <strong>{DateHelper.fullMonthAndYear(advisor.nextThePlanAvailability!)}</strong>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              ) : (
                <></>
              )}
            </div>
          </Col>

          <Col xl="8" lg="7">
            <h1>About {advisor.firstName}</h1>
            <Row className="details-highlights">
              {(advisor.experienceStart ?? now) > fiftyYearsAgo && (advisor.experienceStart ?? now) < now ? (
                <Col lg="12" xl="4" className="text-center">
                  <i className="fa-regular fa-lightbulb att-icon"></i> <strong>{advisor.yearsExperience}</strong> year
                  {advisor.yearsExperience == 1 ? '' : 's'} of experience
                </Col>
              ) : (
                <></>
              )}

              {advisor.eventCount > 0 ? (
                <Col lg="12" xl="4" className="text-center">
                  <i className="fa-regular fa-calendar att-icon"></i> <strong>{advisor.eventCount}</strong> meeting
                  {advisor.eventCount == 1 ? '' : 's'} completed
                </Col>
              ) : (
                <></>
              )}

              {advisor.showReviews && advisor.reviewCount > 0 ? (
                <Col lg="12" xl="4" className="text-center">
                  <i className="fa-solid fa-star att-icon"></i> <strong>{advisor.reviewAverage.toFixed(1)}</strong> average review
                </Col>
              ) : (
                <></>
              )}
            </Row>

            <div className="d-flex">
              <h2>Bio</h2>

              {canEdit ? (
                <div className="flex-grow-1 text-end">
                  <NectarineButton text="Edit Bio" color="blue" iconClassString="fa-light fa-pen-to-square" onClick={toggleEditBioModal} />
                </div>
              ) : (
                <></>
              )}
            </div>

            <div>
              {advisor.longBio ? <p>{advisor.longBio}</p> : <></>}

              {!(advisor.longBio ?? '').length ? <span className="c-gray">No bio has been set yet.</span> : <></>}
            </div>

            {canEdit ? (
              <div>
                <AdvisorEditAttributes
                  show={editAttributesModal}
                  toggle={toggleEditAttributesModal}
                  save={async () => {
                    closeAllModals();
                    await reloadAdvisorDetails();
                  }}
                  advisor={advisor}
                />

                <AdvisorEditBio
                  show={editBioModal}
                  toggle={toggleEditBioModal}
                  save={async () => {
                    closeAllModals();
                    await reloadAdvisorDetails();
                  }}
                  advisor={advisor}
                />
              </div>
            ) : (
              <></>
            )}

            {canEdit ? <hr className="c-gray my-4" /> : <></>}

            <div className="d-flex">
              <h2>{AttributeCategoriesText.ICanHelpYouWith}</h2>

              {canEdit ? (
                <div className="flex-grow-1 text-end">
                  <NectarineButton
                    text="Edit Details"
                    color="blue"
                    iconClassString="fa-light fa-pen-to-square"
                    onClick={toggleEditAttributesModal}
                  />
                </div>
              ) : (
                <></>
              )}
            </div>

            <Row>
              {sortBy(advisor.iCanHelpYouWith, (v) => v.title.toLowerCase()).map((item) => (
                <Col xs="12" xl="6" className="mb-4 d-flex gap-2" key={item.id}>
                  <i className={'fa-light fa-xl att-icon att-details ' + item.iconTag}></i>
                  <div className="att-caption">{item.title}</div>
                </Col>
              ))}

              {!advisor.iCanHelpYouWith.length ? <span className="c-gray">No items were found.</span> : <></>}
            </Row>

            <h2>{AttributeCategoriesText.IEnjoyWorkingWith}</h2>

            <Row>
              {sortBy(advisor.iEnjoyWorkingWith, (v) => v.title.toLowerCase()).map((item) => (
                <Col xs="12" xl="6" className="mb-4 d-flex gap-2" key={item.id}>
                  <i className={'fa-light fa-xl att-icon att-details ' + item.iconTag}></i>
                  <div className="att-caption">{item.title}</div>
                </Col>
              ))}

              {!advisor.iEnjoyWorkingWith.length ? <span className="c-gray">No items were found.</span> : <></>}
            </Row>

            {canEdit ? <hr className="c-gray my-4" /> : <></>}

            <div className="d-flex">
              <h2>Personal Stuff</h2>

              {canEdit ? (
                <div className="flex-grow-1 text-end">
                  <NectarineButton
                    color="blue"
                    iconClassString="fa-light fa-pen-to-square"
                    text="Edit Personal Stuff"
                    onClick={toggleEditBioModal}
                  />
                </div>
              ) : (
                <></>
              )}
            </div>

            {advisor.personalBio ? <p>{advisor.personalBio}</p> : <></>}

            {!(advisor.personalBio ?? '').length ? <span className="c-gray">No personal stuff has been set yet.</span> : <></>}

            {canEdit ? (
              <div className="mb-3 text-end">
                <NectarineButton
                  color="blue"
                  iconClassString="fa-light fa-pen-to-square"
                  text="Edit Photo"
                  onClick={toggleEditPhotoModal}
                />
              </div>
            ) : (
              <></>
            )}

            {advisor.photos.length > 0 ? (
              <Row>
                {advisor.photos.map((item) => (
                  <Col xs="12" className="mb-4" key={item.id}>
                    <img src={item.url} className="personal-photo" />
                  </Col>
                ))}
              </Row>
            ) : (
              <></>
            )}

            {canEdit ? (
              <div>
                <AdvisorEditPersonalPhoto
                  show={editPhotoModal}
                  toggle={toggleEditPhotoModal}
                  save={async () => {
                    closeAllModals();
                    await reloadAdvisorDetails();
                  }}
                  advisor={advisor}
                />

                <AdvisorEditCustomAttributes
                  show={editEducationModal}
                  toggle={toggleEditEducationModal}
                  onSave={async () => {
                    toggleEditEducationModal();

                    await reloadAdvisorDetails();
                  }}
                  categoryId={3}
                  title="Education &amp; Designations"
                  attributes={advisor.education}
                />
              </div>
            ) : (
              <></>
            )}

            {canEdit ? <hr className="c-gray my-4" /> : <></>}

            <div className="d-flex">
              <h2>Education &amp; Designations</h2>

              {canEdit ? (
                <div className="flex-grow-1 text-end">
                  <NectarineButton
                    color="blue"
                    iconClassString="fa-light fa-pen-to-square"
                    text="Edit Education"
                    onClick={toggleEditEducationModal}
                  />
                </div>
              ) : (
                <></>
              )}
            </div>

            <Row>
              {advisor.education.map((item) => (
                <Col xs="12" xl="6" className="mb-4 d-flex gap-2" key={item.id}>
                  <i className={'fa-light fa-xl att-icon att-details ' + item.iconTag}></i>
                  <div className="att-caption">
                    <div className="att-title">{item.title}</div>
                    <div className="att-subtitle">{item.subtitle}</div>
                  </div>
                </Col>
              ))}

              {!advisor.education.length ? <span className="c-gray">No education & designations have been added.</span> : <></>}
            </Row>

            {canEdit ? <hr className="c-gray my-4" /> : <></>}

            <div className="d-flex">
              <h2>Experience</h2>

              {canEdit ? (
                <div className="flex-grow-1 text-end">
                  <NectarineButton
                    color="blue"
                    iconClassString="fa-light fa-pen-to-square"
                    text="Edit Experience"
                    onClick={toggleEditExperienceModal}
                  />
                </div>
              ) : (
                <></>
              )}
            </div>

            {canEdit ? (
              <div>
                <AdvisorEditCustomAttributes
                  show={editExperienceModal}
                  toggle={() => {}}
                  onSave={async () => {
                    toggleEditExperienceModal();

                    await reloadAdvisorDetails();
                  }}
                  categoryId={4}
                  title="Experience"
                  attributes={advisor.experience}
                  sortByFn={(inputItem) => (inputItem.endDate ? new Date(inputItem.endDate) : new Date())}
                  sortByDirection="desc"
                />
              </div>
            ) : (
              <></>
            )}

            <Row>
              {orderBy(advisor.experience, [(v) => (v.endDate ? new Date(v.endDate) : new Date())], ['desc']).map((item) => (
                <Col xs="12" xl="6" className="mb-4 d-flex gap-2" key={item.id}>
                  <i className={'fa-light fa-xl att-icon att-details ' + item.iconTag}></i>
                  <div className="att-caption">
                    <div className="att-title">{item.title}</div>
                    <div className="att-subtitle">{item.subtitle}</div>
                    <div className="att-subtitle">
                      {DateHelper.printYearRange(item.startDate ?? new Date(), item.endDate ?? new Date())}
                    </div>
                  </div>
                </Col>
              ))}

              {!advisor.experience.length ? <span className="c-gray">No experience has been added.</span> : <></>}
            </Row>

            <AdvisorReviews title="Reviews" advisorId={advisor.id} showPrivateComments={false} />

            {!advisor.showReviews && advisor.reviewCount > 0 ? (
              <div>
                <h2>Reviews</h2>
                <p>
                  {advisor.firstName} has received {advisor.reviewCount} review{advisor.reviewCount > 1 && 's'} but{' '}
                  {advisor.reviewCount > 1 ? 'they are' : 'it is'} hidden due to state regulations not allowing the posting of testimonials.
                </p>
              </div>
            ) : (
              <></>
            )}

            <div className="mt-4"></div>
            <h2>Frequently Asked Questions</h2>
            <FAQs />

            <DisclaimerRow />
          </Col>
        </Row>
      ) : (
        <></>
      )}
    </Container>
  );
};

export default AdvisorDetails;
